import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Stethoscope, MessageSquare, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import { consultingAPI } from '../../api';
import { useNavigate } from 'react-router-dom';

const MyConsultings = ({ user }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [consultings, setConsultings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConsultings();
  }, []);

  const loadConsultings = async () => {
    try {
      const data = await consultingAPI.getConsultings();
      setConsultings(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les consultings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              Mes <span className="text-[#C00000]">Consultings</span>
            </h1>
            <p className="text-[#A6A6A6]">Suivez vos demandes de diagnostic</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A6A6A6]">Chargement...</p>
            </div>
          ) : consultings.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="py-12">
                <div className="text-center">
                  <Stethoscope className="h-12 w-12 text-[#A6A6A6] mx-auto mb-4" />
                  <p className="text-[#A6A6A6] mb-4">Aucune demande de consulting pour le moment</p>
                  <Button 
                    onClick={() => window.location.href = '/client/consulting'}
                    className="bg-[#C00000] hover:bg-[#C00000]/90"
                  >
                    Créer une demande
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {consultings.map((consulting) => (
                <Card key={consulting.id} className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white mb-2">
                          {consulting.vehicle_info.brand} {consulting.vehicle_info.model} {consulting.vehicle_info.engine}
                        </CardTitle>
                        <CardDescription className="text-[#A6A6A6]">
                          Consulting {consulting.consulting_id}
                        </CardDescription>
                      </div>
                      {getStatusBadge(consulting.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">Problème décrit</p>
                        <p className="text-white text-sm">{consulting.problem}</p>
                      </div>
                      <div className="flex gap-3 pt-4 border-t border-[#A6A6A6]/20">
                        <Button
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                          onClick={() => navigate(`/client/consultings/${consulting.consulting_id}`)}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Chat avec l'ingénieur
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyConsultings;